import { DATASET_INITIAL_ID } from 'common/constants';
import { CommonFilterObject } from 'common/models/filter.model';
import { RootState } from 'common/store';
import { selectAppliedFilters } from 'common/store/slices/common-filters.slice';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

interface FilterValueItem {
  name: string;
  value: string;
}

export interface FilterLabelItem {
  label: string;
  value: string[];
}

export type FilterLabels = FilterLabelItem[];

interface UseFilterLabelsProps {
  datasetId?: string;
}

/**
 * Custom hook to retrieve and format filter labels based on the applied filters for a given dataset.
 *
 * @param {UseFilterLabelsProps} [props] - The properties object.
 * @param {string} [props.datasetId] - The ID of the dataset to retrieve filters for. Defaults to `DATASET_INITIAL_ID` if not provided.
 * @returns {FilterLabels} An array of filter labels and their corresponding values.
 */
function useFilterLabels({ datasetId }: UseFilterLabelsProps = {}): FilterLabels {
  const appliedFilters = useSelector((state: RootState) =>
    selectAppliedFilters(state, datasetId ?? DATASET_INITIAL_ID)
  );

  const filterKeys = useMemo(() => Object.keys(appliedFilters), [appliedFilters]);

  const filterLabels: FilterLabels = useMemo(() => {
    return filterKeys.reduce((acc: FilterLabels, key: string) => {
      const filter: CommonFilterObject = appliedFilters[key];
      const label = filter.label as string;
      const value = filter.value.map((item: FilterValueItem) => item.name);

      acc.push({ label, value });

      return acc;
    }, []);
  }, [appliedFilters, filterKeys]);

  return filterLabels;
}

export default useFilterLabels;
